/**
 * Compute node copy shared by the Solar V2 presentation's section and the V1
 * brochure's slide, whose English catalog reads it, so both say the same
 * thing. Written to the Vendor Hosting Agreement Rev 2.10. The copy names no
 * dollar figures: each surface shows the gateway's payout beside it. Keys
 * keep the order they render in.
 */

export const SOLAR_AI_COMPUTE_HOW_IT_WORKS = {
  install: {
    title: `We install it indoors`,
    body: `A wall-mounted node in your garage or utility room. It plugs into a standard outlet, like a computer, and connects to your home internet.`,
  },
  online: {
    title: `You keep it online`,
    body: `Keep it powered and on a 1 Gbps internet connection. Vendor monitors it and handles every repair and upgrade.`,
  },
  payout: {
    title: `You get paid monthly`,
    body: `A fixed monthly base plus a network bonus up to your plan's limit, paid by the 15th of the following month.`,
  },
}

/** The answers follow Hosting Agreement Rev 2.10 §1, §2.2, §4–7, §9.1, §10 and §16. */
export const SOLAR_AI_COMPUTE_FAQ = {
  guaranteed: {
    question: `Is the income guaranteed?`,
    answer: `The monthly base is guaranteed for the 30-year term from activation, whether or not your node is busy. The network bonus, up to your plan's limit each month, depends on what the whole Vendor network collects and may be $0. Payouts may be taxable.`,
  },
  bonusThreshold: {
    question: `Does the bonus kick in at a certain utilization?`,
    answer: `No. There's no threshold and no per-hour rate. The bonus grows with what the whole network earns above its costs, so busier months tend to pay more, but even a busy month can pay $0 if costs are high.`,
  },
  payment: {
    question: `How and when am I paid?`,
    answer: `By the 15th of each month for the prior month. Take it as cash, or authorize Vendor to pay part or all of it toward your electricity, internet, solar, financing or battery bills and pay you the rest.`,
  },
  ownership: {
    question: `Who owns and maintains the node?`,
    answer: `Vendor owns it and handles all maintenance, repairs, upgrades and replacements for the full 30 years at no cost to you. Upgrades don't change your base or bonus cap.`,
  },
  cancel: {
    question: `Can I stop hosting?`,
    answer: `The agreement runs 30 years and doesn't include a right to cancel for convenience. You keep any cancellation rights the law gives you, and you and Vendor can agree in writing to end it.`,
  },
  vendorStops: {
    question: `What if Vendor stops operating?`,
    answer: `If Vendor permanently stops providing the service before the term ends, it pays the node's appraised fair market value toward the financing on your solar or battery.`,
  },
  electricity: {
    question: `Who pays for its electricity?`,
    answer: `It runs on your home's power and needs a 1 Gbps internet connection. That electricity is already in your savings estimate, and the fixed base is Vendor's contribution toward those costs.`,
  },
}

/** "What is a compute node?": the money flow and the four questions beneath it. */
export const SOLAR_AI_COMPUTE_STORY = {
  eyebrow: `What is a compute node?`,
  title: `A supercomputer in your home, and you get rewarded for hosting it.`,
  intro: `Demand for computing power is growing faster than it can be built. Your Vendor compute node is a supercomputer that sells its computing time to customers like AI companies, research labs and more. Vendor owns it, finds the customers and pays you every month for hosting it.`,
  flowLabel: `How the money moves`,
  flow: {
    customers: {
      who: `Compute customers`,
      what: `AI companies, research labs and more pay Vendor for computing time`,
      arrow: `buy compute`,
    },
    vendor: {
      who: `Vendor`,
      what: `Sells the capacity and sends the work across its network of compute nodes`,
      arrow: `sends work`,
    },
    node: {
      who: `Your compute node`,
      what: `Runs the work on your home's power and internet`,
      arrow: `rewards you monthly`,
    },
    you: {
      who: `You`,
      what: `A fixed base every month, plus a share of network earnings up to your plan's limit`,
    },
  },
  questions: {
    why: {
      label: `Why`,
      title: `The world needs more computing power.`,
      body: `Demand for compute is outpacing supply. Hosting a node puts more of it to work and gets you paid for providing the home it runs in.`,
    },
    what: {
      label: `What it does`,
      title: `Sells computing time to Vendor's customers.`,
      body: `AI companies, research labs and other customers buy time on the network. You don't manage anything; Vendor finds the work and keeps the node running.`,
    },
    who: {
      label: `Who pays for it`,
      title: `Vendor owns it and pays for its upkeep.`,
      body: `No purchase, lease or loan. Vendor pays for maintenance, repairs, upgrades and replacements for 30 years. You provide the space, the electricity and a 1 Gbps internet connection.`,
    },
    earn: {
      label: `How you earn`,
      title: `A fixed base, plus a reward when the network does well.`,
      body: `The base is paid every month whether or not your node is busy. When the network earns a surplus, half of it is shared across hosts as a bonus, up to your plan's limit each month.`,
    },
  },
}

/** "How the network bonus works", in plain words (Rev 2.10 §2.2). */
export const SOLAR_AI_COMPUTE_BONUS = {
  eyebrow: `How the network bonus works`,
  lead: `Think of the bonus as profit sharing. Your node is one of many in Vendor's network. When the network brings in more money than it costs to run, Vendor shares half of the extra with its hosts. Bigger plans get a bigger share, up to their plan's limit.`,
  strong: {
    label: `A strong month`,
    title: `The network earns more than it costs.`,
    body: `Half of the extra goes to hosts, so you get your base plus a bonus.`,
  },
  slow: {
    label: `A slow month`,
    title: `The network only covers its costs.`,
    body: `There's nothing extra to share, so there's no bonus that month. Your base is still paid.`,
  },
  notes: {
    network: {
      title: `It's about the whole network.`,
      body: `How busy your own node is doesn't change your bonus.`,
    },
    carryOver: {
      title: `Slow months don't carry over.`,
      body: `A slow month never reduces a later one.`,
    },
    statement: {
      title: `You can see the math.`,
      body: `Your monthly statement shows what the network earned, its costs and your share.`,
    },
  },
}
